'use client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocale, useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Tabs } from '@//components/Tabs';
import { WidgetBox } from '@//components/WidgetBox';
import useEvCharchingCostPerKilometer, {
  type UseEvCharchingCostPerKilometerVariables,
} from '@//data/ev-calculation/useEvCharchingCostPerKilometer';
import useVehiclesByIds from '@//data/search/vehicles/useVehiclesByIds';
import { useLegacyTrackChargingCostCustomizeClick } from '@//data/tracking/useLegacyTrackChargingCostCustomizeClick';
import { useLegacyTrackChargingCostParameterChange } from '@//data/tracking/useLegacyTrackChargingCostParameterChange';
import useDebouncedValue from '@//hooks/useDebouncedValue';
import { useMarketMetadata } from '@//providers/MarketSettingsProvider';
import { formatCurrency } from '@//utils/currency';
import { ItemPageSection } from '../ItemPageSection';
import { WidgetHeading } from '../WidgetHeading';
import { OwnPrice } from './OwnPrice';
import { TemplatePrice } from './TemplatePrice';
import useValidateDebouncedKwhCost from './useValidateDebouncedKwhCost';
import {
  calculateCostPerMonth,
  DefaultDistanceValue,
  DistanceOptions,
  EvCalculationSchema,
  formToPayload,
  getDefaultValues,
} from './utils';

interface ChargingCostSectionProps {
  id: string;
}

const ChargingCostSection: React.FC<ChargingCostSectionProps> = ({ id }) => {
  const locale = useLocale();
  const t = useTranslations('pages.item.chargingCostsSection');
  const { currency } = useMarketMetadata();
  const defaultValues = useMemo(() => getDefaultValues(id), [id]);
  const { control, register, setValue, getValues } = useForm({
    defaultValues,
    resolver: zodResolver(EvCalculationSchema),
  });

  // Fetch vehicle data for tracking
  const { data: vehiclesData } = useVehiclesByIds({ ids: [id] });
  const vehicle = vehiclesData?.[0]?.vehicle;

  // Initialize tracking hooks
  const { trackLegacyChargingCostCustomizeClick } = useLegacyTrackChargingCostCustomizeClick();
  const { trackLegacyChargingCostParameterChange } = useLegacyTrackChargingCostParameterChange();

  const [distance, kwhCost, view] = useWatch({
    control,
    name: ['distance', 'kwhCost', 'view'],
  });

  const debouncedKwhCost = useDebouncedValue(kwhCost, 500);

  const debouncedLoading = useValidateDebouncedKwhCost({
    kwhCost,
    debouncedKwhCost,
    view,
  });

  const evChargingCostPerKilometerVariables = useMemo(
    (): UseEvCharchingCostPerKilometerVariables =>
      formToPayload({
        id,
        view,
        kwhCost: debouncedKwhCost,
      }),
    [id, view, debouncedKwhCost],
  );

  const evChargingCostQuery = useEvCharchingCostPerKilometer(evChargingCostPerKilometerVariables);
  const data = evChargingCostQuery.data;

  const kwhCostValue = data?.description.costPerKwh.value;
  useEffect(() => {
    if (kwhCost === undefined && kwhCostValue !== undefined && !Number.isNaN(kwhCostValue)) {
      setValue('kwhCost', kwhCostValue * 100);
    }
  }, [kwhCostValue]);

  const costPerKilometer = data?.description.costPerKilometer.value;

  const costPerMonth = useMemo(() => {
    if (evChargingCostQuery.isFetching || debouncedLoading) return undefined;

    // For template price view, always calculate if we have data
    if (view === 'default' && costPerKilometer) {
      return calculateCostPerMonth(costPerKilometer, distance);
    }

    // For own price view, only calculate if kwhCost is provided
    if (view === 'own' && kwhCost && costPerKilometer) {
      return calculateCostPerMonth(costPerKilometer, distance);
    }

    return undefined;
  }, [costPerKilometer, evChargingCostQuery.isFetching, debouncedLoading, kwhCost, distance, view]);

  const costPerMonthLabel = useMemo(() => {
    if (!costPerMonth) return t('unknownCost');
    return formatCurrency(costPerMonth, {
      locale,
      currency,
      period: 'month',
    });
  }, [costPerMonth, locale, currency, t]);

  const _onChangeDistance = useCallback(([d]: [number]) => {
    setValue('distance', d);
  }, []);

  const minDistance = DistanceOptions[0];
  const maxDistance = DistanceOptions[DistanceOptions.length - 1];

  const distanceOptions = DistanceOptions.map((option) => ({
    value: option.toString(),
    label: t('distanceUnit', { count: option }),
  }));

  const selectedDistance = distanceOptions.find(
    (option) => option.value === distance.toString(),
  )?.label;

  const onBlurDistance = useCallback(() => {
    const value = getValues('distance');
    if (Number.isNaN(value)) {
      setValue('distance', minDistance);
    } else if (value > maxDistance) {
      setValue('distance', maxDistance);
    }
  }, [getValues, setValue, minDistance, maxDistance]);

  // Tracking callback for distance change (range slider and select)
  const onChangeDistanceWithTracking = useCallback(
    (parameter: 'distance-slider' | 'distance-dropdown') =>
      ([d]: [number]) => {
        setValue('distance', d);

        // Track parameter change
        if (vehicle && selectedDistance) {
          trackLegacyChargingCostParameterChange({
            vehicle,
            information: {
              context: 'modal',
              parameter,
              disance: selectedDistance,
              chargingCost: costPerMonth,
            },
          });
        }
      },
    [setValue, vehicle, selectedDistance, trackLegacyChargingCostParameterChange, t, costPerMonth],
  );

  // Tracking callback for cost input change
  const onChangeCostInputWithTracking = useCallback(
    (_value: number) => {
      // Track parameter change
      if (vehicle && selectedDistance) {
        trackLegacyChargingCostParameterChange({
          vehicle,
          information: {
            context: 'modal',
            parameter: 'cost-input',
            disance: selectedDistance,
            chargingCost: costPerMonth,
          },
        });
      }
    },
    [vehicle, selectedDistance, trackLegacyChargingCostParameterChange, t, costPerMonth],
  );

  return (
    <ItemPageSection id="section-ev-charging" heading={t('heading')}>
      <WidgetBox>
        <WidgetHeading description={t('description')}>{t('widgetHeading')}</WidgetHeading>
        <Tabs
          id="tabs-ev-charging"
          overflowPadding="var(--_box-padding-inline)"
          onTabChange={(tab) => {
            setValue('view', tab === 0 ? 'default' : 'own');

            // Track when user clicks the "own price" tab
            if (tab === 1 && vehicle) {
              trackLegacyChargingCostCustomizeClick({
                vehicle,
                information: {
                  label: t('tabs.ownPrice'),
                  context: 'inline',
                  disance: distance.toString(),
                  chargingCost: costPerMonth,
                },
              });
            }
          }}
          tabs={[
            {
              label: t('tabs.templatePrice'),
              children: (
                <TemplatePrice
                  minDistance={minDistance}
                  maxDistance={maxDistance}
                  distance={distance}
                  distanceOptions={distanceOptions}
                  data={data}
                  costPerMonth={costPerMonth}
                  costPerMonthLabel={costPerMonthLabel}
                  selectedDistance={selectedDistance}
                  loading={evChargingCostQuery.isFetching || debouncedLoading}
                  defaultDistanceValue={DefaultDistanceValue}
                  onChangeDistanceSlider={onChangeDistanceWithTracking('distance-slider')}
                  onChangeDistanceSelect={onChangeDistanceWithTracking('distance-dropdown')}
                  onBlurDistance={onBlurDistance}
                  register={register}
                />
              ),
            },
            {
              label: t('tabs.ownPrice'),
              children: (
                <OwnPrice
                  control={control}
                  minDistance={minDistance}
                  maxDistance={maxDistance}
                  distance={distance}
                  distanceOptions={distanceOptions}
                  data={data}
                  costPerMonth={costPerMonth}
                  costPerMonthLabel={costPerMonthLabel}
                  selectedDistance={selectedDistance}
                  loading={evChargingCostQuery.isFetching || debouncedLoading}
                  defaultDistanceValue={DefaultDistanceValue}
                  onChangeDistanceSlider={onChangeDistanceWithTracking('distance-slider')}
                  onChangeDistanceSelect={onChangeDistanceWithTracking('distance-dropdown')}
                  onChangeCostInput={onChangeCostInputWithTracking}
                  onBlurDistance={onBlurDistance}
                  register={register}
                />
              ),
            },
          ]}
        />
      </WidgetBox>
    </ItemPageSection>
  );
};

export default ChargingCostSection;
